import { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Cropper from 'react-easy-crop';
import { uploadImage, generateAvatarPath } from '../../utils/storage';
import ModalCloseButton from '../Modals/ModalCloseButton';

const ImageUpload = ({
  currentImageUrl,
  onImageChange,
  type = 'player', // 'player' or 'club'
  id = '',
  aspect = 1, // 1:1 for circular avatars
  circularCrop = true,
}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setImageSrc(reader.result);
      setShowCropModal(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    });
    reader.readAsDataURL(file);
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.src = url;
    });

  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    // Set canvas size to match the crop area
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // Draw the cropped portion of the image
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          throw new Error('Canvas is empty');
        }
        resolve(blob);
      }, 'image/jpeg', 0.9);
    });
  };

  const handleCropComplete = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    setIsUploading(true);
    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
      const path = generateAvatarPath(type, id || 'temp');
      const downloadURL = await uploadImage(croppedImage, path);
      
      onImageChange(downloadURL);
      setShowCropModal(false);
      setImageSrc(null);
    } catch (error) {
      console.error('Error cropping and uploading image:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to upload image. Please try again.';
      
      if (error.code === 'storage/unauthorized') {
        errorMessage = 'Upload failed: Permission denied. Please check Firebase Storage rules.';
      } else if (error.code === 'storage/canceled') {
        errorMessage = 'Upload was canceled.';
      } else if (error.code === 'storage/unknown') {
        errorMessage = 'An unknown error occurred. Please check your connection and try again.';
      } else if (error.message) {
        errorMessage = `Upload failed: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancelCrop = () => {
    setShowCropModal(false);
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  return (
    <div className="image-upload-container">
      <div className="flex flex-col items-center gap-md">
        <div className="relative">
          <img
            src={currentImageUrl || 'https://placehold.co/128x128/e5e7eb/6b7280?text=Avatar'}
            alt="Avatar preview"
            className={`avatar avatar-xl avatar-border ${circularCrop ? 'rounded-full' : ''}`}
            style={{ objectFit: 'cover' }}
          />
        </div>

        <label className="btn btn-secondary cursor-pointer">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            disabled={isUploading}
          />
          <span className="btn-text">
            {isUploading ? 'UPLOADING...' : 'UPLOAD IMAGE'}
          </span>
        </label>

        {/* {currentImageUrl && (
          <button
            type="button"
            onClick={() => onImageChange('')}
            className="btn btn-secondary"
            disabled={isUploading}
          >
            <span className="btn-text">REMOVE IMAGE</span>
          </button>
        )} */}
      </div>

      {/* Portalled to <body>: when opened from another modal, that modal's transform animation would
          otherwise trap this full-screen overlay inside its panel (cropped, buttons hidden). */}
      {showCropModal && imageSrc && createPortal(
        <div className="modal-overlay crop-overlay">
          <div className="modal modal-wide crop-modal" role="dialog" aria-modal="true" aria-label="Crop image">
            <ModalCloseButton onClick={isUploading ? undefined : handleCancelCrop} />

            <h3 className="modal-title">Crop Image</h3>

            <div className="crop-modal__stage">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                cropShape={circularCrop ? 'round' : 'rect'}
                showGrid={false}
              />
            </div>

            <label className="crop-modal__zoom">
              <span className="material-symbols-outlined" aria-hidden="true">zoom_out</span>
              <input
                type="range"
                value={zoom}
                min={1}
                max={3}
                step={0.01}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                aria-label="Zoom"
              />
              <span className="material-symbols-outlined" aria-hidden="true">zoom_in</span>
            </label>
            <p className="crop-modal__hint">Drag to position · pinch or slide to zoom</p>

            <div className="modal-footer crop-modal__footer">
              <button
                type="button"
                onClick={handleCancelCrop}
                className="btn btn-secondary"
                disabled={isUploading}
              >
                <span className="btn-text">CANCEL</span>
              </button>
              <button
                type="button"
                onClick={handleCropComplete}
                className="btn btn-primary"
                disabled={isUploading || !croppedAreaPixels}
              >
                <span className="btn-text">
                  {isUploading ? 'UPLOADING...' : 'SAVE'}
                </span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ImageUpload;

